'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@module/mdrt-org/shared/components/button/button';
import { Dialog } from '@module/mdrt-org/shared/components/dialog/dialog';
import { FieldController } from '@module/mdrt-org/shared/components/field-controller/field-controller';
import { useGlobalLoaderContext } from '@module/mdrt-org/shared/components/providers/global-loader-context';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { SelectField } from '@module/mdrt-org/shared/components/select-field/select-field';
import { MAX_AGENT_ID_LENGTH, MAX_JOB_TITLE_LENGTH, MAX_PERSONAL_COMPANY_NAME_LENGTH, MAX_YEAR_LENGTH, MIN_YEAR_OF_FIRST_LICENSE } from '@module/mdrt-org/shared/utils/constants/inputs-constants';
import { patchAccountSettingsAction } from '@module/mdrt-org/shared/utils/data/patch-account-settings-action';
import { AccountSettingsFormStep } from '@module/mdrt-org/shared/utils/enums/account-settings-form-step';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import { allowOnlyNumbersTransform } from '@module/mdrt-org/shared/utils/helpers/allow-only-numbers';
import { transformYearOfFirstLicenceDefaultValue } from '@module/mdrt-org/shared/utils/helpers/transform-default-values';
import { useAddDefaultErrorToast } from '@module/mdrt-org/shared/utils/hooks/use-add-default-error-toast';
import { useConfirmNavigation } from '@module/mdrt-org/shared/utils/hooks/use-confirm-navigation';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { type Company } from '@module/mdrt-org/shared/utils/types/company';
import { logOut } from '@shared/misc/next-auth/log-out';
import { DialogActions } from '@shared/ui-components/dialog/components/dialog-actions/dialog-actions';
import { FormInput } from '@shared/ui-components/form-input/form-input';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { ModalTrigger } from '@shared/ui-components/modal/modal';
import { RenderHTML } from '@shared/ui-components/render-html/render-html';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { GTM_ACTION_CLICK, GTM_EVENT_ACCOUNT_PROFILE_UPDATE, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { ButtonType } from '@shared/utils/enums/button-type';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { type SelectOption } from '@shared/utils/types/select-option';
import { type ServerActionResponse } from '@shared/utils/types/server-action';
import { removeUserRoleAction } from '../../actions/remove-user-role-action';
import { useAccountSettingsPageContext } from '../../providers/account-settings-page-context';
import { CompanyType } from '../../utils/enums/company-type';
import { type ProfessionalInfoDTO, type ProfessionalInfoFormData } from '../../utils/types/form-data';
import { type AccountSettingsProfessionalInfoContent } from './get-account-settings-professional-info-content';
import styles from './professional-info-form.module.scss';
type ProfessionalInfoFormType = {
  cmsContent: AccountSettingsProfessionalInfoContent;
  companiesData: Company[];
  designationsData: SelectOption[];
  tabUrl: string;
};
export const ProfessionalInfoForm = t0 => {
  const $ = _c(137);
  const {
    cmsContent,
    companiesData,
    designationsData,
    tabUrl
  } = t0;
  const {
    hasCompanyAdminRole,
    personId
  } = useUserContext();
  const {
    countriesData,
    customerProfileContextData,
    resetCustomerProfileContextData,
    setCustomerProfileContextData
  } = useAccountSettingsPageContext();
  const addErrorToast = useAddDefaultErrorToast();
  const {
    setIsNonBlockingLoaderVisible
  } = useGlobalLoaderContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date().getFullYear();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const currentYear = t1;
  const minYear = currentYear - MIN_YEAR_OF_FIRST_LICENSE;
  let affiliatedCompaniesOptions;
  let brokerDealerCompaniesOptions;
  let t2;
  if ($[1] !== companiesData || $[2] !== customerProfileContextData.country || $[3] !== customerProfileContextData.countryId) {
    const companiesInSelectedCountry = customerProfileContextData.country ? companiesData.filter(t3 => {
      const {
        countryID
      } = t3;
      return countryID === customerProfileContextData.countryId;
    }) : [];
    const getCompaniesOptions = companyType => companiesInSelectedCountry?.filter(company => company.countryID === customerProfileContextData.countryId && company.companyType === companyType).map(_temp);
    affiliatedCompaniesOptions = getCompaniesOptions(CompanyType.COMPANY_AFFILIATION);
    brokerDealerCompaniesOptions = getCompaniesOptions(CompanyType.BROKER_DEALER);
    t2 = getCompaniesOptions(CompanyType.AGENCY);
    $[1] = companiesData;
    $[2] = customerProfileContextData.country;
    $[3] = customerProfileContextData.countryId;
    $[4] = affiliatedCompaniesOptions;
    $[5] = brokerDealerCompaniesOptions;
    $[6] = t2;
  } else {
    affiliatedCompaniesOptions = $[4];
    brokerDealerCompaniesOptions = $[5];
    t2 = $[6];
  }
  const agencyCompaniesOptions = t2;
  let t3;
  if ($[7] !== companiesData) {
    t3 = companyId => {
      const company_1 = companiesData.find(companyData => companyData.id === companyId);
      return company_1 ? {
        label: company_1.name,
        value: company_1.id
      } : null;
    };
    $[7] = companiesData;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const getCompanyDetails = t3;
  let t4;
  if ($[9] !== customerProfileContextData.affiliatedCompanyId || $[10] !== getCompanyDetails) {
    t4 = getCompanyDetails(customerProfileContextData.affiliatedCompanyId);
    $[9] = customerProfileContextData.affiliatedCompanyId;
    $[10] = getCompanyDetails;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const currentAffiliatedCompany = t4;
  let t5;
  if ($[12] !== customerProfileContextData.brokerDealerCompanyId || $[13] !== getCompanyDetails) {
    t5 = getCompanyDetails(customerProfileContextData.brokerDealerCompanyId);
    $[12] = customerProfileContextData.brokerDealerCompanyId;
    $[13] = getCompanyDetails;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const currentBrokerDealerCompany = t5;
  let t6;
  if ($[15] !== customerProfileContextData.agencyCompanyId || $[16] !== getCompanyDetails) {
    t6 = getCompanyDetails(customerProfileContextData.agencyCompanyId);
    $[15] = customerProfileContextData.agencyCompanyId;
    $[16] = getCompanyDetails;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const currentAgencyCompany = t6;
  const t7 = currentAffiliatedCompany || null;
  const t8 = currentAgencyCompany || null;
  const t9 = customerProfileContextData.agentId || "";
  const t10 = currentBrokerDealerCompany || null;
  let t11;
  if ($[18] !== countriesData || $[19] !== customerProfileContextData.countryId) {
    t11 = countriesData.find(country => country.countryId === customerProfileContextData.countryId)?.name || "";
    $[18] = countriesData;
    $[19] = customerProfileContextData.countryId;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== customerProfileContextData.designations || $[22] !== designationsData) {
    t12 = designationsData && customerProfileContextData?.designations && customerProfileContextData.designations.map(designationId => designationsData.find(option => option?.value === designationId)).filter(Boolean) || null;
    $[21] = customerProfileContextData.designations;
    $[22] = designationsData;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = customerProfileContextData?.jobTitle || "";
  const t14 = customerProfileContextData.personalCompany || "";
  let t15;
  if ($[24] !== customerProfileContextData.yearOfFirstLicense) {
    t15 = transformYearOfFirstLicenceDefaultValue(customerProfileContextData.yearOfFirstLicense);
    $[24] = customerProfileContextData.yearOfFirstLicense;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t12 || $[29] !== t13 || $[30] !== t14 || $[31] !== t15 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
    t16 = {
      ...formBaseConfig,
      defaultValues: {
        affiliatedCompanyId: t7,
        agencyCompanyId: t8,
        agentId: t9,
        brokerDealerCompanyId: t10,
        country: t11,
        designations: t12,
        jobTitle: t13,
        personalCompany: t14,
        yearOfFirstLicense: t15
      }
    };
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const formMethods = useForm(t16);
  const {
    formState: t17,
    handleSubmit,
    reset,
    resetField
  } = formMethods;
  const {
    dirtyFields,
    isDirty
  } = t17;
  let t18;
  if ($[36] !== addErrorToast || $[37] !== resetCustomerProfileContextData) {
    t18 = result => {
      if (result.status !== 200 && "message" in result) {
        resetCustomerProfileContextData();
        addErrorToast();
      }
    };
    $[36] = addErrorToast;
    $[37] = resetCustomerProfileContextData;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const onFailure = t18;
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [AccountSettingsMutationKey.PATCH_ACCOUNT_SETTINGS];
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== formMethods || $[41] !== reset) {
    t20 = () => reset({
      ...formMethods.getValues()
    });
    $[40] = formMethods;
    $[41] = reset;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== onFailure || $[44] !== t20) {
    t21 = {
      mutationFunction: patchAccountSettingsAction,
      mutationKey: t19,
      onFailure,
      onSuccessHandler: t20
    };
    $[43] = onFailure;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const {
    isMutationPending,
    mutate
  } = useMutationHandler(t21);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  let t22;
  if ($[46] !== customerProfileContextData || $[47] !== mutate || $[48] !== personId || $[49] !== setCustomerProfileContextData) {
    t22 = async data => {
      const formData = {
        affiliatedCompanyId: data.affiliatedCompanyId ? Number(data.affiliatedCompanyId?.value) : null,
        agencyCompanyId: data.agencyCompanyId ? Number(data.agencyCompanyId?.value) : null,
        agentId: data.agentId,
        brokerDealerCompanyId: data.brokerDealerCompanyId ? Number(data.brokerDealerCompanyId?.value) : null,
        designations: data?.designations?.length ? data?.designations.map(_temp2) : [],
        jobTitle: data.jobTitle,
        personalCompany: data.personalCompany,
        yearOfFirstLicense: data.yearOfFirstLicense ? data.yearOfFirstLicense : null
      };
      setCustomerProfileContextData({
        ...customerProfileContextData,
        ...formData
      });
      mutate({
        formData: {
          accountSettingStep: AccountSettingsFormStep.ProfessionalInformation,
          professionalData: formData
        },
        personId
      });
      sendGtmEvent({
        action: GTM_ACTION_CLICK,
        event: GTM_EVENT_USER_INTERACTIONS,
        eventName: GTM_EVENT_ACCOUNT_PROFILE_UPDATE,
        label: "Professional Information"
      });
    };
    $[46] = customerProfileContextData;
    $[47] = mutate;
    $[48] = personId;
    $[49] = setCustomerProfileContextData;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  const completeFormSubmit = t22;
  let t23;
  if ($[51] !== addErrorToast || $[52] !== reset) {
    t23 = result_0 => {
      if (result_0.status !== 200 && "message" in result_0) {
        reset();
        addErrorToast();
      }
    };
    $[51] = addErrorToast;
    $[52] = reset;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== completeFormSubmit || $[55] !== handleSubmit) {
    t24 = async () => {
      await handleSubmit(async data_0 => {
        await completeFormSubmit(data_0);
      })();
      await logOut();
    };
    $[54] = completeFormSubmit;
    $[55] = handleSubmit;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t23 || $[58] !== t24) {
    t25 = {
      mutationFunction: removeUserRoleAction,
      onFailure: t23,
      onSuccessHandler: t24,
      showSuccessToast: false
    };
    $[57] = t23;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  const {
    isMutationPending: isUserRoleRemovePending,
    mutate: removeUserRole
  } = useMutationHandler(t25);
  let t26;
  if ($[60] !== removeUserRole) {
    t26 = () => {
      setIsConfirmationModalOpen(false);
      removeUserRole(undefined);
    };
    $[60] = removeUserRole;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  const onCompanyChangeConfirm = t26;
  let t27;
  if ($[62] !== completeFormSubmit || $[63] !== dirtyFields.affiliatedCompanyId || $[64] !== hasCompanyAdminRole) {
    t27 = async data_1 => {
      if (dirtyFields.affiliatedCompanyId && hasCompanyAdminRole) {
        setIsConfirmationModalOpen(true);
        return;
      }
      await completeFormSubmit(data_1);
    };
    $[62] = completeFormSubmit;
    $[63] = dirtyFields.affiliatedCompanyId;
    $[64] = hasCompanyAdminRole;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  const onSubmit = t27;
  let t28;
  let t29;
  if ($[66] !== isUserRoleRemovePending || $[67] !== setIsNonBlockingLoaderVisible) {
    t28 = () => {
      setIsNonBlockingLoaderVisible(isUserRoleRemovePending);
    };
    t29 = [isUserRoleRemovePending, setIsNonBlockingLoaderVisible];
    $[66] = isUserRoleRemovePending;
    $[67] = setIsNonBlockingLoaderVisible;
    $[68] = t28;
    $[69] = t29;
  } else {
    t28 = $[68];
    t29 = $[69];
  }
  useEffect(t28, t29);
  useConfirmNavigation(isDirty, tabUrl);
  let t30;
  if ($[70] !== cmsContent.tabTitle) {
    t30 = <h1 className={styles["professional-info-form__heading"]}>{cmsContent.tabTitle}</h1>;
    $[70] = cmsContent.tabTitle;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== cmsContent.yearOfFirstLicenseValidationLabel) {
    t31 = {
      max: {
        message: cmsContent.yearOfFirstLicenseValidationLabel,
        value: currentYear
      },
      validate: {
        minYearValidation: value => value && Number(value) < minYear ? cmsContent.yearOfFirstLicenseValidationLabel : true
      }
    };
    $[72] = cmsContent.yearOfFirstLicenseValidationLabel;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <FormInput />;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== cmsContent.yearOfFirstLicenseLabel || $[76] !== t31) {
    t33 = <FieldController label={cmsContent.yearOfFirstLicenseLabel} maxLength={MAX_YEAR_LENGTH} name="yearOfFirstLicense" rules={t31} transform={allowOnlyNumbersTransform}>{t32}</FieldController>;
    $[75] = cmsContent.yearOfFirstLicenseLabel;
    $[76] = t31;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <FormInput disabled={true} />;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== cmsContent.countryHint || $[80] !== cmsContent.countryLabel) {
    t35 = <FieldController hint={cmsContent.countryHint} label={cmsContent.countryLabel} name="country">{t34}</FieldController>;
    $[79] = cmsContent.countryHint;
    $[80] = cmsContent.countryLabel;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  const t36 = !customerProfileContextData.country;
  let t37;
  if ($[82] !== affiliatedCompaniesOptions || $[83] !== cmsContent.affiliatedCompanyLabel || $[84] !== t36) {
    t37 = <SelectField isClearable={true} isDisabled={t36} label={cmsContent.affiliatedCompanyLabel} name="affiliatedCompanyId" options={affiliatedCompaniesOptions} />;
    $[82] = affiliatedCompaniesOptions;
    $[83] = cmsContent.affiliatedCompanyLabel;
    $[84] = t36;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  const t38 = !customerProfileContextData.country;
  let t39;
  if ($[86] !== brokerDealerCompaniesOptions || $[87] !== cmsContent.brokerDealerCompanyLabel || $[88] !== t38) {
    t39 = <SelectField isClearable={true} isDisabled={t38} label={cmsContent.brokerDealerCompanyLabel} name="brokerDealerCompanyId" options={brokerDealerCompaniesOptions} />;
    $[86] = brokerDealerCompaniesOptions;
    $[87] = cmsContent.brokerDealerCompanyLabel;
    $[88] = t38;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  const t40 = !customerProfileContextData.country;
  let t41;
  if ($[90] !== agencyCompaniesOptions || $[91] !== cmsContent.agencyCompanyLabel || $[92] !== t40) {
    t41 = <SelectField isClearable={true} isDisabled={t40} label={cmsContent.agencyCompanyLabel} name="agencyCompanyId" options={agencyCompaniesOptions} />;
    $[90] = agencyCompaniesOptions;
    $[91] = cmsContent.agencyCompanyLabel;
    $[92] = t40;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  let t42;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <FormInput maxLength={MAX_PERSONAL_COMPANY_NAME_LENGTH} />;
    $[94] = t42;
  } else {
    t42 = $[94];
  }
  let t43;
  if ($[95] !== cmsContent.personalCompanyLabel) {
    t43 = <FieldController label={cmsContent.personalCompanyLabel} name="personalCompany">{t42}</FieldController>;
    $[95] = cmsContent.personalCompanyLabel;
    $[96] = t43;
  } else {
    t43 = $[96];
  }
  let t44;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <FormInput maxLength={MAX_JOB_TITLE_LENGTH} />;
    $[97] = t44;
  } else {
    t44 = $[97];
  }
  let t45;
  if ($[98] !== cmsContent.jobTitleLabel) {
    t45 = <FieldController label={cmsContent.jobTitleLabel} name="jobTitle">{t44}</FieldController>;
    $[98] = cmsContent.jobTitleLabel;
    $[99] = t45;
  } else {
    t45 = $[99];
  }
  let t46;
  if ($[100] !== cmsContent.designationsHint || $[101] !== cmsContent.designationsLabel || $[102] !== designationsData) {
    t46 = <SelectField hint={cmsContent.designationsHint} isMulti={true} label={cmsContent.designationsLabel} maximumSelectedOptions={2} name="designations" options={designationsData} />;
    $[100] = cmsContent.designationsHint;
    $[101] = cmsContent.designationsLabel;
    $[102] = designationsData;
    $[103] = t46;
  } else {
    t46 = $[103];
  }
  let t47;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <FormInput maxLength={MAX_AGENT_ID_LENGTH} />;
    $[104] = t47;
  } else {
    t47 = $[104];
  }
  let t48;
  if ($[105] !== cmsContent.agentIdLabel) {
    t48 = <FieldController label={cmsContent.agentIdLabel} name="agentId">{t47}</FieldController>;
    $[105] = cmsContent.agentIdLabel;
    $[106] = t48;
  } else {
    t48 = $[106];
  }
  const t49 = !isDirty || isMutationPending;
  let t50;
  if ($[107] !== cmsContent.saveChanges || $[108] !== t49) {
    t50 = <div className={styles["professional-info-form__footer"]}><Button disabled={t49} type={ButtonType.SUBMIT}>{cmsContent.saveChanges}</Button></div>;
    $[107] = cmsContent.saveChanges;
    $[108] = t49;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  if ($[110] !== formMethods || $[111] !== onSubmit || $[112] !== t30 || $[113] !== t33 || $[114] !== t35 || $[115] !== t37 || $[116] !== t39 || $[117] !== t41 || $[118] !== t43 || $[119] !== t45 || $[120] !== t46 || $[121] !== t48 || $[122] !== t50) {
    t51 = <FormWrapper className={styles["professional-info-form"]} dataTestId="professional-info-form" formMethods={formMethods} onSubmit={onSubmit}>{t30}{t33}{t35}{t37}{t39}{t41}{t43}{t45}{t46}{t48}{t50}</FormWrapper>;
    $[110] = formMethods;
    $[111] = onSubmit;
    $[112] = t30;
    $[113] = t33;
    $[114] = t35;
    $[115] = t37;
    $[116] = t39;
    $[117] = t41;
    $[118] = t43;
    $[119] = t45;
    $[120] = t46;
    $[121] = t48;
    $[122] = t50;
    $[123] = t51;
  } else {
    t51 = $[123];
  }
  let t52;
  if ($[124] !== cmsContent.cancel || $[125] !== cmsContent.companyChangeAlertBody || $[126] !== cmsContent.companyChangeAlertTitle || $[127] !== cmsContent.confirm || $[128] !== onCompanyChangeConfirm || $[129] !== resetField) {
    t52 = () => <Dialog className={styles["confirmation-modal"]} footer={<DialogActions onPrimaryButtonClick={onCompanyChangeConfirm} onSecondaryButtonClick={() => {
      resetField("affiliatedCompanyId");
      setIsConfirmationModalOpen(false);
    }} primaryButtonLabel={cmsContent.confirm} secondaryButtonLabel={cmsContent.cancel} />} hasCloseButton={false} title={cmsContent.companyChangeAlertTitle}><RenderHTML className={styles["confirmation-modal__content"]} html={cmsContent.companyChangeAlertBody} /></Dialog>;
    $[124] = cmsContent.cancel;
    $[125] = cmsContent.companyChangeAlertBody;
    $[126] = cmsContent.companyChangeAlertTitle;
    $[127] = cmsContent.confirm;
    $[128] = onCompanyChangeConfirm;
    $[129] = resetField;
    $[130] = t52;
  } else {
    t52 = $[130];
  }
  let t53;
  if ($[131] !== isConfirmationModalOpen || $[132] !== t52) {
    t53 = <ModalTrigger isOpen={isConfirmationModalOpen}>{t52}</ModalTrigger>;
    $[131] = isConfirmationModalOpen;
    $[132] = t52;
    $[133] = t53;
  } else {
    t53 = $[133];
  }
  let t54;
  if ($[134] !== t51 || $[135] !== t53) {
    t54 = <>{t51}{t53}</>;
    $[134] = t51;
    $[135] = t53;
    $[136] = t54;
  } else {
    t54 = $[136];
  }
  return t54;
};
function _temp(company_0) {
  return {
    label: company_0.name,
    value: `${company_0.id}`
  };
}
function _temp2(designation) {
  return Number(designation?.value);
}